import React, {
  ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Card, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { RequestState, useAsyncOperationState } from '../../../../dorian-shared/hooks/useAsyncOperationState';
import { api } from '../../../api';
import { PageWrapper } from '../../../ui/PageWrapper';
import { formatOfferListDateTime } from '../offerTimeZoneConversion';
import { usePersistedDisplayTimeZone } from '../usePersistedDisplayTimeZone';
import { DeleteButton } from './DeleteButton';
import { DuplicateButton } from './DuplicateButton';

function getPageLayout() {
  return {
    header: { title: 'Offers', settings: 'admin' },
    sidebar: {
      nav: [
        {
          title: 'Back',
          href: '/home/',
          action: window.history.length > 1 && document.referrer !== window.location.href
            ? () => { window.history.back(); } : null,
          variant: 'secondary',
        },
        { title: 'New offer', href: '/offers/new', variant: 'primary' },
      ],
    },
  };
}

interface Entity { id: string | number }

function titleLinkFormatter(value: ReactNode, entity: Entity) {
  return <Link to={`/offers/${entity.id}`}>{value}</Link>;
}

interface OfferRow extends Entity {
  id: number
  start_time?: string | null
  end_time?: string | null
}

const useColumns = (onDeleteSuccess: (offerId: number) => void, timeZone: string) => useMemo(
  () => [
    { dataField: 'display_name', text: 'Name', formatter: titleLinkFormatter },
    { dataField: 'identifier', text: 'Identifier' },
    { dataField: 'product_slug', text: 'Product' },
    { dataField: 'active', text: 'Active' },
    {
      dataField: 'start_time',
      text: `Start date (${timeZone})`,
      formatter: (value: string | null) => formatOfferListDateTime(value, timeZone),
    },
    {
      dataField: 'end_time',
      text: `End date (${timeZone})`,
      formatter: (value: string | null) => formatOfferListDateTime(value, timeZone),
    },
    {
      dataField: 'id',
      text: 'Actions',
      formatter: (_offerId: number, row: OfferRow) => (
        <div className="d-flex">
          <DuplicateButton offerId={row.id} />
          <DeleteButton offerId={row.id} onSuccess={onDeleteSuccess} />
        </div>
      ),
    },
  ],
  [onDeleteSuccess, timeZone],
);

function useOffers() {
  const [requestState, { setToLoading, setToError, setToSuccess }] = useAsyncOperationState();
  const [offers, setOffers] = useState<OfferRow[]>([]);
  useEffect(() => {
    setToLoading();
    api.get('/v1/offers')
      .then((response) => setOffers(response.data.offers))
      .then(() => setToSuccess())
      .catch(() => setToError());
  }, [setToError, setToLoading, setToSuccess]);
  const deleteOfferLocally = useCallback(
    (offerId: number) => setOffers((current) => current.filter((offer) => offer.id !== offerId)),
    [],
  );
  return {
    requestState, offers, deleteOfferLocally,
  };
}

function OfferList() {
  const { requestState, offers, deleteOfferLocally } = useOffers();
  const { timeZone } = usePersistedDisplayTimeZone();
  const columns = useColumns(deleteOfferLocally, timeZone);
  switch (requestState) {
    case RequestState.Success:
      return (
        <BootstrapTable
          bootstrap4
          keyField="id"
          data={offers}
          columns={columns}
        />
      );
    case RequestState.Loading:
      return (
        <div className="text-center">
          <Spinner variant="primary" animation="border" className="loadingSpinner justify-content-center" />
        </div>
      );
    case RequestState.Error:
      return (
        <div className="text-center">
          Unable to load offers. Refresh the page to try again.
        </div>
      );
    default:
      return null;
  }
}

export function OfferListPage(props: RouteComponentProps) {
  const pageLayout = getPageLayout();
  return (
    <PageWrapper {...props} page={pageLayout}>
      <Card>
        <Card.Body>
          <Card.Title>Offers</Card.Title>
          <OfferList />
        </Card.Body>
      </Card>
    </PageWrapper>
  );
}
